import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ExternalLink, Info, Link2, Users, StickyNote, FolderOpen, Building2, Loader2 } from "lucide-react";
import { vendorsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScrollFade } from "@/components/ScrollFade";
import { VendorLogo } from "@/components/vendors/VendorLogo";
import { VendorStatusBadge, LastChanged } from "@/components/vendors/vendorUtils";
import { VendorGeneralTab } from "@/components/vendors/VendorGeneralTab";
import { VendorLinksTab, VendorContactsTab } from "@/components/vendors/VendorLinksContacts";
import { VendorNotesTab } from "@/components/vendors/VendorNotesTab";
import { VendorDocumentsTab } from "@/components/vendors/VendorDocumentsTab";
import { VendorCustomersTab } from "@/components/vendors/VendorCustomersTab";

const TAB = "gap-2";

export default function VendorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role !== "readonly";
  const [vendor, setVendor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [counts, setCounts] = useState({});
  const setCount = useCallback((k) => (n) => setCounts((c) => (c[k] === n ? c : { ...c, [k]: n })), []);
  const [onNotes] = useState(() => setCount("notes"));
  const [onDocs] = useState(() => setCount("documents"));
  const [onCustomers] = useState(() => setCount("customers"));

  useEffect(() => {
    setVendor(null);
    vendorsApi.get(id).then((r) => setVendor(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const patchItems = (patch) => setVendor((v) => ({ ...v, ...patch, updated_at: new Date().toISOString(), updated_by_name: user?.full_name }));

  if (notFound) return <div className="py-16 text-center space-y-4"><p className="text-muted-foreground">Hersteller nicht gefunden.</p><Button variant="outline" onClick={() => navigate("/medical-software")}>Zur Übersicht</Button></div>;
  if (!vendor) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const c = (k) => (counts[k] !== undefined ? ` (${counts[k]})` : "");
  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendor-profile">
      <div className="flex flex-wrap items-start gap-3 sm:gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/medical-software")} data-testid="vendor-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <VendorLogo vendor={vendor} size="w-12 h-12 sm:w-16 sm:h-16" text="text-xl sm:text-2xl" />
        <div className="min-w-0 flex-1 basis-48">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold break-words min-w-0" data-testid="vendor-profile-name">{vendor.name}</h1>
            <VendorStatusBadge status={vendor.status} testId="vendor-profile-status" />
          </div>
          <p className="text-muted-foreground mt-1">{vendor.description || vendor.category || "Softwarehersteller"}</p>
          <LastChanged item={vendor} testId="vendor-profile-last-changed" className="mt-1" />
        </div>
        {vendor.website && (
          <Button variant="outline" onClick={() => window.open(vendor.website, "_blank", "noopener,noreferrer")} data-testid="vendor-profile-website"><ExternalLink className="w-4 h-4 mr-2" />Website</Button>
        )}
      </div>

      <Tabs defaultValue="general" className="space-y-5">
        <ScrollFade>
          <TabsList>
            <TabsTrigger value="general" className={TAB} data-testid="vendor-tab-general"><Info className="w-4 h-4" />Allgemein</TabsTrigger>
            <TabsTrigger value="links" className={TAB} data-testid="vendor-tab-links"><Link2 className="w-4 h-4" />Links ({vendor.links?.length || 0})</TabsTrigger>
            <TabsTrigger value="contacts" className={TAB} data-testid="vendor-tab-contacts"><Users className="w-4 h-4" />Ansprechpartner ({vendor.contacts?.length || 0})</TabsTrigger>
            <TabsTrigger value="notes" className={TAB} data-testid="vendor-tab-notes"><StickyNote className="w-4 h-4" />Notizen{c("notes")}</TabsTrigger>
            <TabsTrigger value="documents" className={TAB} data-testid="vendor-tab-documents"><FolderOpen className="w-4 h-4" />Dokumente{c("documents")}</TabsTrigger>
            <TabsTrigger value="customers" className={TAB} data-testid="vendor-tab-customers"><Building2 className="w-4 h-4" />Kundenpraxen{c("customers")}</TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="general"><VendorGeneralTab vendor={vendor} onChange={setVendor} canEdit={canEdit} /></TabsContent>
        <TabsContent value="links"><VendorLinksTab vendor={vendor} onItemsChange={patchItems} isAdmin={isAdmin} canEdit={canEdit} /></TabsContent>
        <TabsContent value="contacts"><VendorContactsTab vendor={vendor} onItemsChange={patchItems} isAdmin={isAdmin} canEdit={canEdit} /></TabsContent>
        <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden"><VendorNotesTab vendorId={vendor.id} isAdmin={isAdmin} canEdit={canEdit} onCount={onNotes} /></TabsContent>
        <TabsContent value="documents" forceMount className="data-[state=inactive]:hidden"><VendorDocumentsTab vendorId={vendor.id} isAdmin={isAdmin} canEdit={canEdit} onCount={onDocs} /></TabsContent>
        <TabsContent value="customers" forceMount className="data-[state=inactive]:hidden"><VendorCustomersTab vendorId={vendor.id} onCount={onCustomers} /></TabsContent>
      </Tabs>
    </div>
  );
}
